/*
|---------------------------------------------------------------------
| Tickets Realtime Vuex Module
|---------------------------------------------------------------------
|
| Suscripción GLOBAL (a nivel de sesión, no atada al ciclo de vida de una
| página) al canal privado 'tickets-table', donde el backend emite dos
| eventos con payload:
|
|   - tickets.reserva.actualizada: cambio de reserva de un ticket
|     (reservar/liberar/reasignar). Se aplica en vivo a la card del listado
|     (board-app/actualizarReservaTicket) y se expone via lastReservaEvent
|     para que el detalle de ticket abierto lo consuma con un watch.
|   - tickets.comentario.creado: un usuario no-cliente comentó un ticket.
|     Si el usuario actual es el dueño de la reserva y no es el autor, se
|     muestra un aviso (activeNotification).
|
| activeNotification es la notificación "rica" (con link al ticket) que
| consume TicketRealtimeNotification.vue — independiente del toast genérico
| de app/showToast, que se usa para mensajes cortos de una sola línea en
| todo el resto del portal.
|
| Debe suscribirse en sesión (login/auth2fa y App.vue created, para cubrir
| F5 con sesión persistida) porque el aviso de comentario debe funcionar en
| cualquier pantalla del portal, no sólo con el listado de tickets abierto.
|
*/
const TICKETS_TABLE_CHANNEL = 'tickets-table'
const RESERVA_EVENT = '.tickets.reserva.actualizada'
const COMENTARIO_EVENT = '.tickets.comentario.creado'

const state = {
    lastReservaEvent: null,
    lastComentarioEvent: null,
    activeNotification: null,
    // Guard de idempotencia: subscribeTicketsRealtime se dispara tanto en
    // login/auth2fa como en App.vue created() — sin esto, si ambas llegan a
    // correr en la misma sesión, el canal queda escuchado dos veces (cada
    // evento se procesaría por duplicado).
    subscribed: false,
}

const mutations = {
    SET_LAST_RESERVA_EVENT: (state, payload) => {
        state.lastReservaEvent = payload
    },

    SET_LAST_COMENTARIO_EVENT: (state, payload) => {
        state.lastComentarioEvent = payload
    },

    SET_ACTIVE_NOTIFICATION: (state, payload) => {
        state.activeNotification = payload
    },

    CLEAR_ACTIVE_NOTIFICATION: (state) => {
        state.activeNotification = null
    },

    SET_SUBSCRIBED: (state, value) => {
        state.subscribed = value
    },
}

const actions = {
    subscribeTicketsRealtime({ commit, dispatch, state }) {
        if (typeof window === 'undefined' || !window.Echo) return
        if (state.subscribed) return
        try {
            window.Echo.private(TICKETS_TABLE_CHANNEL)
                .listen(RESERVA_EVENT, (e) => {
                    commit('SET_LAST_RESERVA_EVENT', { ...e, _receivedAt: Date.now() })
                    commit('board-app/actualizarReservaTicket', e, { root: true })
                    dispatch('reactToReservaQuitada', e)
                })
                .listen(COMENTARIO_EVENT, (e) => {
                    commit('SET_LAST_COMENTARIO_EVENT', { ...e, _receivedAt: Date.now() })
                    dispatch('reactToComentarioCreado', e)
                })
                // pusher:subscription_error: la conexión de bajo nivel puede seguir
                // "connected" mientras la autorización de ESTE canal privado falló
                // (p. ej. token vencido tras una reconexión) — sin este listener,
                // eso queda invisible: se ven los pings de la conexión pero nunca
                // llega ningún evento de tickets-table, sin ningún error visible.
                .error((error) => {
                    console.warn('Fallo al autorizar el canal tickets-table (WebSocket):', error)
                })
            commit('SET_SUBSCRIBED', true)
        } catch (error) {
            // Echo es opcional; si falla, el front sigue funcionando sin tiempo real.
            console.warn('No se pudo suscribir a tickets-table:', error)
        }
    },

    reactToComentarioCreado({ rootGetters, commit }, payload) {
        const user = rootGetters.isAuthenticated?.user
        if (!user) return

        // El filtro "autor no-cliente" ya lo garantiza el backend (nunca emite
        // este evento para comentarios del cliente); acá sólo validamos que
        // el ticket sea mío y que no sea yo mismo quien comentó. Comparación no
        // estricta: los ids del payload llegan numéricos desde el WebSocket,
        // user.id puede llegar como string desde la API (mismo motivo del ==
        // en TicketDetailPage.vue / tickets/mutations.js).
        const esMiReserva = payload.reservado_por_id == user.id
        const esOtroAutor = payload.autor?.id != user.id

        if (esMiReserva && esOtroAutor) {
            // tipo_mensaje: 0 = respuesta al cliente, 1 = nota interna (ver
            // TicketsComentarioCreado). Tono neutral tipo notificación de
            // sistema, no conversacional.
            const accion = payload.tipo_mensaje == 1
                ? 'agregó una nota interna en'
                : 'respondió al cliente en'

            commit('SET_ACTIVE_NOTIFICATION', {
                type: 'comentario',
                ticketId: payload.ticket_id,
                title: 'Nueva actividad en el ticket',
                message: `${payload.autor.nombre} ${accion} el ticket #${payload.ticket_id}.`,
            })
        }
    },

    // Backend: TicketsMensajesReservadosObserver sólo llena perdio_reserva_id
    // cuando fue OTRO usuario quien reasignó o liberó la reserva — liberarla/
    // reasignarla uno mismo nunca dispara este aviso (ver docblock del Observer).
    reactToReservaQuitada({ rootGetters, commit }, payload) {
        const user = rootGetters.isAuthenticated?.user
        if (!user || payload.perdio_reserva_id == null) return
        if (payload.perdio_reserva_id != user.id) return

        const detalle = payload.reservado && payload.reservado_por
            ? `El ticket #${payload.ticket_id} fue reasignado a ${payload.reservado_por.nombre}.`
            : `La reserva del ticket #${payload.ticket_id} fue liberada.`

        commit('SET_ACTIVE_NOTIFICATION', {
            type: 'reserva-quitada',
            ticketId: payload.ticket_id,
            title: 'Reserva actualizada',
            message: detalle,
        })
    },

    unsubscribeTicketsRealtime({ commit }) {
        commit('SET_SUBSCRIBED', false)
        if (typeof window === 'undefined' || !window.Echo) return
        try {
            window.Echo.leave(TICKETS_TABLE_CHANNEL)
        } catch (error) {
            // noop
        }
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
}
